import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import StudioLayout from "@/components/layouts/StudioLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Globe, Loader2, Copy, Check, CheckCircle2, Clock, Trash2, ExternalLink, ShieldCheck, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";

function CopyField({ label, value }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_e) {
      toast.error("Couldn't copy — select and copy manually");
    }
  };
  return (
    <div>
      {label && <p className="text-[10px] uppercase tracking-[0.25em] text-white/35 mb-1">{label}</p>}
      <div className="flex items-stretch border border-white/12 bg-black/40">
        <code className="flex-1 px-3 py-2 text-xs text-white/85 break-all font-mono">{value}</code>
        <button
          type="button"
          onClick={copy}
          className="px-3 border-l border-white/12 text-white/60 hover:text-white hover:bg-white/5 transition-colors"
          data-testid={`copy-${(label || value).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          aria-label="Copy"
        >
          {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
        </button>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  if (status === "verified") {
    return (
      <Badge className="rounded-none bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 gap-1.5 uppercase tracking-widest text-[10px]">
        <CheckCircle2 className="w-3 h-3" /> Verified &amp; live
      </Badge>
    );
  }
  if (status === "pending") {
    return (
      <Badge className="rounded-none bg-amber-500/15 text-amber-300 border border-amber-500/30 gap-1.5 uppercase tracking-widest text-[10px]">
        <Clock className="w-3 h-3" /> Awaiting verification
      </Badge>
    );
  }
  return (
    <Badge className="rounded-none bg-white/5 text-white/50 border border-white/15 uppercase tracking-widest text-[10px]">
      Not connected
    </Badge>
  );
}

export default function StudioDomains() {
  const [tenant, setTenant] = useState(null);
  const [domainState, setDomainState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [removing, setRemoving] = useState(false);

  const load = async () => {
    try {
      const [t, d] = await Promise.all([
        api.get("/tenant/me"),
        api.get("/tenant/me/domain"),
      ]);
      setTenant(t.data);
      setDomainState(d.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async () => {
    if (!input.trim()) return;
    setConnecting(true);
    try {
      const { data } = await api.post("/tenant/me/domain", { domain: input.trim() });
      setDomainState(data);
      setInput("");
      toast.success("Domain added — now add the DNS records below");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setConnecting(false);
    }
  };

  const verify = async () => {
    setVerifying(true);
    try {
      const { data } = await api.post("/tenant/me/domain/verify");
      setDomainState(data);
      if (data.verified) {
        toast.success("Domain verified — your storefront is now live on it!");
        await load();
      } else {
        toast.error(data.message || "Not verified yet");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setVerifying(false);
    }
  };

  const remove = async () => {
    setRemoving(true);
    try {
      const { data } = await api.delete("/tenant/me/domain");
      setDomainState(data);
      toast.success("Domain disconnected");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setRemoving(false);
    }
  };

  if (loading || !tenant) {
    return (
      <StudioLayout tenant={tenant}>
        <div className="flex items-center gap-2 text-white/40 text-sm uppercase tracking-widest">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      </StudioLayout>
    );
  }

  const status = domainState?.custom_domain_status || "none";
  const domain = domainState?.custom_domain || "";
  const records = domainState?.dns?.records || [];
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const defaultUrl = `${origin}/s/${tenant.slug}`;
  // Custom domains are gated until the Cloudflare-for-SaaS routing layer is set up.
  const CUSTOM_DOMAINS_ENABLED = process.env.REACT_APP_CUSTOM_DOMAINS_ENABLED === "true";

  return (
    <StudioLayout tenant={tenant}>
      <div className="max-w-3xl">
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-9 h-9 border border-white/15 flex items-center justify-center" style={{ color: tenant.theme_color }}>
                <Globe className="w-4 h-4" />
              </div>
              <h1 className="font-display text-2xl font-bold tracking-tight" data-testid="domains-title">Custom domain</h1>
            </div>
            <p className="text-sm text-white/50 max-w-xl leading-relaxed">
              {CUSTOM_DOMAINS_ENABLED
                ? <>Put <span className="text-white">{tenant.name}</span> on your own domain. Viewers land straight on your storefront — no <code className="text-white/70">/s/{tenant.slug}</code> path.</>
                : <>Your storefront is <span className="text-white">live now</span> at your Vawworld address below. Connecting your own domain (e.g. yourbrand.com) is <span className="text-white">coming soon</span>.</>}
            </p>
          </div>
          {CUSTOM_DOMAINS_ENABLED
            ? <StatusBadge status={status} />
            : <Badge className="rounded-none bg-amber-500/15 text-amber-300 border border-amber-500/30 uppercase tracking-widest text-[10px]" data-testid="domains-comingsoon-badge">Coming soon</Badge>}
        </div>

        {/* Live storefront URL (path-based) */}
        <div className="border border-white/10 bg-white/[0.02] p-4 mb-6 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.25em] text-white/35 mb-1">Your live storefront URL</p>
            <code className="text-sm text-white/80 break-all">{defaultUrl}</code>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <button
              type="button"
              onClick={async () => { try { await navigator.clipboard.writeText(defaultUrl); toast.success("Storefront URL copied"); } catch (_e) { toast.error("Copy failed"); } }}
              className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1.5 uppercase tracking-widest"
              data-testid="domains-copy-url"
            >
              <Copy className="w-3 h-3" /> Copy
            </button>
            <a href={defaultUrl} target="_blank" rel="noreferrer" className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1.5 uppercase tracking-widest" data-testid="domains-default-link">
              Open <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>

        {/* Coming-soon panel (custom domains not yet enabled) */}
        {!CUSTOM_DOMAINS_ENABLED && (
          <div className="border border-white/10 bg-white/[0.02] p-6" data-testid="domains-comingsoon-card">
            <div className="flex items-start gap-3">
              <Globe className="w-5 h-5 text-amber-300 mt-0.5 shrink-0" />
              <div className="text-sm text-white/60 leading-relaxed">
                <p className="text-white font-semibold mb-1">Bring your own domain — coming soon</p>
                Soon you&apos;ll be able to point your own domain (like <code className="text-white/70">yourbrand.com</code>) at
                your storefront with automatic HTTPS, in a few clicks. For now, share your live URL above — it works
                everywhere today, no setup needed.
              </div>
            </div>
          </div>
        )}

        {/* STATE: none -> connect form */}
        {CUSTOM_DOMAINS_ENABLED && status === "none" && (
          <div className="border border-white/10 bg-white/[0.02] p-6" data-testid="domains-connect-card">
            <h2 className="font-display text-base font-semibold mb-1">Connect a domain</h2>
            <p className="text-sm text-white/45 mb-4">Enter a domain you already own (e.g. idlichutnee.com).</p>
            <div className="flex gap-2">
              <Input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && connect()}
                placeholder="idlichutnee.com"
                className="rounded-none bg-black/40 border-white/15 focus-visible:ring-0 focus-visible:border-white/40 text-white"
                data-testid="domains-input"
              />
              <Button
                onClick={connect}
                disabled={connecting || !input.trim()}
                className="rounded-none bg-white text-black hover:bg-white/90 uppercase tracking-widest text-xs shrink-0"
                data-testid="domains-connect-button"
              >
                {connecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Connect <ArrowRight className="w-3.5 h-3.5 ml-1.5" /></>}
              </Button>
            </div>
          </div>
        )}

        {/* STATE: pending / verified -> show domain + DNS */}
        {CUSTOM_DOMAINS_ENABLED && status !== "none" && (
          <div className="space-y-6">
            {/* connected domain row */}
            <div className="border border-white/10 bg-white/[0.02] p-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                {status === "verified"
                  ? <ShieldCheck className="w-5 h-5 text-emerald-400 shrink-0" />
                  : <Clock className="w-5 h-5 text-amber-400 shrink-0" />}
                <div className="min-w-0">
                  <p className="text-sm text-white font-semibold truncate">{domain}</p>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-white/35">
                    {status === "verified" ? "Live on this domain" : "Add the DNS records, then verify"}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {status === "verified" && (
                  <a href={`https://${domain}`} target="_blank" rel="noreferrer" className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1.5 uppercase tracking-widest" data-testid="domains-visit-link">
                    Visit <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                <Button variant="ghost" size="sm" onClick={remove} disabled={removing} className="rounded-none text-white/50 hover:text-red-400 h-8" data-testid="domains-remove-button">
                  {removing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                </Button>
              </div>
            </div>

            {/* DNS instructions (only until verified — kept visible for reference) */}
            {records.length > 0 && (
              <div className="border border-white/10 bg-white/[0.02] p-6" data-testid="domains-dns-card">
                <h2 className="font-display text-base font-semibold mb-1">
                  {status === "verified" ? "DNS records (for reference)" : "Add these DNS records"}
                </h2>
                <p className="text-sm text-white/45 mb-3">
                  Log in to your domain registrar (GoDaddy, Namecheap, Cloudflare…) and add the records below.
                  DNS changes can take a few minutes to propagate.
                </p>
                <div className="mb-5 border-l-2 border-emerald-400/50 bg-emerald-400/[0.06] px-3 py-2 text-xs text-emerald-200/90">
                  <span className="font-semibold">Verification only needs the TXT record.</span> Add the TXT record first,
                  then click Verify. The A / CNAME records are for routing traffic (see note below).
                </div>

                <div className="space-y-5">
                  {records.map((rec, i) => (
                    <div key={i} className="border border-white/8 bg-black/30 p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Badge className="rounded-none bg-white/10 text-white border-0 uppercase tracking-widest text-[10px]">{rec.type}</Badge>
                        <span className="text-[10px] uppercase tracking-[0.25em] text-white/35">
                          {rec.purpose === "verification" ? "Ownership check" : "Traffic routing"}
                        </span>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-3">
                        <CopyField label="Name / Host" value={rec.name} />
                        <CopyField label="Value / Points to" value={rec.value} />
                      </div>
                      <p className="text-[10px] text-white/30 mt-2">TTL: {rec.ttl}s (or Auto)</p>
                    </div>
                  ))}
                </div>

                {status !== "verified" && (
                  <div className="mt-6 flex items-center gap-3">
                    <Button
                      onClick={verify}
                      disabled={verifying}
                      className="rounded-none bg-white text-black hover:bg-white/90 uppercase tracking-widest text-xs"
                      data-testid="domains-verify-button"
                    >
                      {verifying ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Checking DNS…</> : <>Verify domain <ShieldCheck className="w-3.5 h-3.5 ml-1.5" /></>}
                    </Button>
                    {domainState?.message && domainState?.verified === false && (
                      <p className="text-xs text-amber-300/80" data-testid="domains-verify-message">{domainState.message}</p>
                    )}
                  </div>
                )}

                {domainState?.dns?.note && (
                  <p className="mt-5 text-[11px] text-white/40 leading-relaxed border-t border-white/8 pt-4" data-testid="domains-dns-note">
                    <span className="text-white/60 font-medium">Routing note: </span>{domainState.dns.note}
                  </p>
                )}

                {status === "verified" && domainState?.routing_ok === false && (
                  <p className="text-xs text-amber-300/80 mt-4">
                    Ownership is verified. Traffic routing check: {domainState.routing_message}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Help note */}
        {CUSTOM_DOMAINS_ENABLED && (
          <div className="mt-8 border-t border-white/10 pt-6">
            <p className="text-[11px] uppercase tracking-[0.25em] text-white/30 mb-2">How it works</p>
            <ol className="text-sm text-white/50 space-y-1.5 list-decimal list-inside leading-relaxed">
              <li>Enter your domain and hit Connect.</li>
              <li>Add the CNAME (routing) and TXT (ownership) records at your registrar.</li>
              <li>Click Verify. Once verified, your storefront goes live on your domain automatically with HTTPS.</li>
            </ol>
          </div>
        )}
      </div>
    </StudioLayout>
  );
}
